import { useState, useRef, useEffect } from "react";
import { Play } from "lucide-react";

// Minimal, premium video block:
// - Poster shown until "playing"
// - No native controls (custom minimalist play button)
// - Autoplay muted + loop optional (for hero backgrounds pass autoplay/muted/loop=true)
// - Lazy loaded via IntersectionObserver
// - Graceful fallback: if no src, shows poster only (or nothing)
export default function VideoBlock({
    src = "",
    poster = "",
    ratio = "16/9",
    autoplay = false,
    loop = false,
    muted = true,
    className = "",
    rounded = "1rem",
    testid = "video-block",
    alt = "",
}) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    const [playing, setPlaying] = useState(autoplay);

    useEffect(() => {
        if (!ref.current) return;
        const io = new IntersectionObserver(
            (entries) => entries.forEach(e => {
                if (e.isIntersecting) setInView(true);
                // Auto-pause video when leaving the viewport (perf + UX)
                const v = ref.current && ref.current.querySelector("video");
                if (v) {
                    if (e.isIntersecting) { if (autoplay && v.paused) v.play().catch(()=>{}); }
                    else { if (!v.paused) v.pause(); }
                }
            }),
            { rootMargin: "300px", threshold: 0.1 }
        );
        io.observe(ref.current);
        return () => io.disconnect();
    }, [autoplay]);

    // No video source? Show elegant still-poster only.
    if (!src) {
        return (
            <div ref={ref} data-testid={testid} className={`relative overflow-hidden ${className}`} style={{ aspectRatio: ratio, borderRadius: rounded, background: "#03102A" }}>
                {poster ? (
                    <img src={poster} alt={alt} loading="lazy" className="w-full h-full object-cover"/>
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/40 text-xs uppercase tracking-widest">Vidéo à venir</div>
                )}
            </div>
        );
    }

    return (
        <div ref={ref} data-testid={testid} className={`relative overflow-hidden group ${className}`} style={{ aspectRatio: ratio, borderRadius: rounded, background: "#03102A" }}>
            {inView && (
                <video
                    src={src}
                    poster={poster || undefined}
                    autoPlay={autoplay}
                    muted={muted}
                    loop={loop}
                    playsInline
                    preload={autoplay ? "auto" : "metadata"}
                    controls={playing && !autoplay}
                    className="w-full h-full object-cover"
                    onPlay={() => setPlaying(true)}
                />
            )}
            {!autoplay && !playing && (
                <button
                    onClick={(e) => {
                        const v = e.currentTarget.previousSibling;
                        if (v && v.play) { v.play(); setPlaying(true); }
                    }}
                    className="absolute inset-0 w-full h-full flex items-center justify-center"
                    aria-label="Lire la vidéo"
                    data-testid={`${testid}-play`}
                >
                    {poster && <img src={poster} alt={alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover"/>}
                    <span className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(4,17,38,0.25), rgba(4,17,38,0.55))" }}/>
                    <span className="relative w-14 h-14 rounded-full flex items-center justify-center transition-transform group-hover:scale-105" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37 55%,#B8912B)", boxShadow: "0 8px 24px -6px rgba(212,175,55,0.6), inset 0 1px 0 rgba(255,255,255,0.5)" }}>
                        <Play className="w-5 h-5 text-[#041126]" fill="#041126"/>
                    </span>
                </button>
            )}
        </div>
    );
}

// Hero video background — 6-12s loop, muted, autoplay, poster fallback.
// Overlay uses a strong left-heavy gradient AND a soft uniform bottom shade to
// keep the night-blue premium mood even when very bright frames appear in the
// video (Safari/iOS especially may render brighter than expected).
export function HeroVideoBackground({
    src = "",
    poster = "",
    overlay = "linear-gradient(100deg, rgba(3,16,42,0.96) 0%, rgba(4,17,38,0.88) 45%, rgba(4,17,38,0.72) 100%)",
}) {
    if (!src) {
        return poster ? (
            <div className="absolute inset-0">
                <img src={poster} alt="" aria-hidden="true" className="w-full h-full object-cover" loading="eager"/>
                <div className="absolute inset-0" style={{ background: overlay }}/>
            </div>
        ) : null;
    }
    return (
        <div className="absolute inset-0">
            <video
                poster={poster || undefined}
                autoPlay muted loop playsInline preload="auto"
                // iOS Safari sometimes ignores autoPlay unless webkit-playsinline is set + muted attribute is present at hydration
                // Using both attributes for maximum compatibility.
                // @ts-ignore — non-standard attribute accepted by iOS
                webkit-playsinline="true"
                x5-playsinline="true"
                disablePictureInPicture
                controlsList="nodownload noplaybackrate nofullscreen"
                className="w-full h-full object-cover"
                data-testid="hero-video"
            >
                {/* Explicit source with MIME type — improves compatibility across hosts that
                    do not always return an accurate Content-Type header on .mp4 files. */}
                <source src={src} type="video/mp4"/>
            </video>
            <div className="absolute inset-0" style={{ background: overlay }}/>
        </div>
    );
}
